import { useEffect, useState } from "react";
import { API, api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileDown, Search, Printer } from "lucide-react";
import { toast } from "sonner";

const TYPE_OPTS = [["all", "All"], ["purchase", "Purchase"], ["sale", "Sale"], ["jobcard", "Job Card"], ["transfer", "Stock Transfer"], ["adjustment", "Stock Adjustment"]];

export const ItemRegisterView = ({ itemId, onClose }) => {
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [ttype, setTtype] = useState("all");
  const [loading, setLoading] = useState(false);

  const params = () => {
    const p = new URLSearchParams();
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (ttype !== "all") p.set("txn_type", ttype);
    return p.toString();
  };

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/items/${itemId}/register?${params()}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (itemId) load(); }, [itemId]); // eslint-disable-line

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API}/items/${itemId}/register/pdf?${params()}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (e) { toast.error(e.message || "PDF failed"); }
  };

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["Date", "Transaction Type", "Reference No", "Party Name", "Quantity In", "Quantity Out", "Rate", "Value", "Running Balance"];
    const lines = data.rows.map(m => [
      (m.date || "").slice(0, 10), m.type_label, m.ref, m.party,
      m.qty > 0 ? m.qty : "", m.qty < 0 ? -m.qty : "", m.rate,
      Math.round(Math.abs(m.qty) * m.rate * 100) / 100, m.closing,
    ]);
    const csv = [head, ...lines].map(r => r.map(esc).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url;
    a.download = `transaction_register_${(data.item.item_name || "item").replace(/\s+/g, "_")}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const it = data?.item;
  return (
    <div data-testid="item-register">
      <div className="flex items-center justify-between border-b border-border pb-3">
        <div className="flex items-center gap-3">
          {onClose && <Button type="button" data-testid="register-back-btn" variant="outline" onClick={onClose} className="rounded-sm text-xs uppercase tracking-wider print:hidden"><ArrowLeft className="w-3 h-3 mr-1"/>Back</Button>}
          <h2 className="font-display uppercase text-lg">Transaction Register</h2>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button data-testid="register-export-btn" variant="outline" onClick={exportCsv} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/>Export CSV</Button>
          <Button data-testid="register-print-btn" variant="outline" onClick={() => window.print()} className="rounded-sm text-xs uppercase tracking-wider"><Printer className="w-3 h-3 mr-1"/>Print</Button>
          <Button data-testid="register-pdf-btn" variant="outline" onClick={downloadPdf} className="rounded-sm text-xs uppercase tracking-wider"><FileDown className="w-3 h-3 mr-1"/>PDF</Button>
          {onClose && <Button data-testid="register-close-btn" variant="ghost" onClick={onClose} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground">Close</Button>}
        </div>
      </div>

      {it && (
        <div className="grid grid-cols-5 gap-3 border border-border bg-secondary/30 rounded-sm p-3 mt-3 text-xs" data-testid="register-header">
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Item Name</div><div className="font-semibold" data-testid="reg-item-name">{it.item_name}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Item Code</div><div className="font-mono" data-testid="reg-item-code">{it.item_code || "-"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Part No</div><div className="font-mono" data-testid="reg-part-no">{it.part_number || "-"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">HSN Code</div><div className="font-mono" data-testid="reg-hsn">{it.hsn_code || "-"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current Stock</div><div className="font-mono font-bold text-primary" data-testid="reg-stock">{it.stock} {it.unit}</div></div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 border border-border bg-card rounded-sm p-3 mt-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">From</Label>
          <Input data-testid="reg-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">To</Label>
          <Input data-testid="reg-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 rounded-sm bg-secondary border-border h-9 w-40"/>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Transaction Type</Label>
          <Select value={ttype} onValueChange={setTtype}>
            <SelectTrigger data-testid="reg-type" className="mt-1 rounded-sm bg-secondary border-border h-9 w-44"><SelectValue/></SelectTrigger>
            <SelectContent>{TYPE_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button data-testid="reg-apply-btn" onClick={load} disabled={loading} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Search className="w-3 h-3 mr-1"/>Search</Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden mt-3">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Transaction Type</th>
              <th className="text-left p-3">Reference No</th>
              <th className="text-left p-3">Party Name</th>
              <th className="text-right p-3">Quantity In</th>
              <th className="text-right p-3">Quantity Out</th>
              <th className="text-right p-3">Rate</th>
              <th className="text-right p-3">Value</th>
              <th className="text-right p-3">Running Balance</th>
            </tr>
          </thead>
          <tbody>
            {(!data || data.rows.length === 0) && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">{loading ? "Loading…" : "No transactions for this item"}</td></tr>}
            {data?.rows.map((m, i) => (
              <tr key={i} data-testid={`reg-row-${i}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3 font-mono text-xs text-muted-foreground">{(m.date || "").slice(0, 10)}</td>
                <td className="p-3 text-xs">{m.type_label}</td>
                <td className="p-3 font-mono text-xs text-primary">{m.ref}</td>
                <td className="p-3 text-xs">{m.party}</td>
                <td className="p-3 text-right font-mono text-green-500">{m.qty > 0 ? m.qty : ""}</td>
                <td className="p-3 text-right font-mono text-red-500">{m.qty < 0 ? -m.qty : ""}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(m.rate)}</td>
                <td className="p-3 text-right font-mono text-xs">{inr(Math.abs(m.qty) * m.rate)}</td>
                <td className="p-3 text-right font-mono font-semibold">{m.closing}</td>
              </tr>
            ))}
          </tbody>
          {data && data.rows.length > 0 && (
            <tfoot className="bg-secondary/60 text-xs font-semibold">
              <tr className="border-t border-border">
                <td className="p-3" colSpan={4}>Totals</td>
                <td className="p-3 text-right font-mono text-green-500" data-testid="reg-total-in">{data.total_in}</td>
                <td className="p-3 text-right font-mono text-red-500" data-testid="reg-total-out">{data.total_out}</td>
                <td className="p-3 text-right text-muted-foreground" colSpan={2}>Current Stock</td>
                <td className="p-3 text-right font-mono font-bold text-primary" data-testid="reg-closing-stock">{data.current_stock}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
